// An inline "?" beside a label that carries a rule. It replays steps of the
// page tour over the thing they describe: same copy (tours/tourSteps.js),
// same spotlight, so there is one explanation of each rule to keep right.
//
// Used sparingly, on the labels where a rule changes a decision or a row is
// full of marks that need decoding. Not on the Freezes and 5x Play cells,
// which already open their own sheet.
import React, { useEffect, useRef } from "react";
import { HelpCircle } from "lucide-react";
import { spotlightSteps } from "../tours/tourDriver";

// step: one tour step's data-tour name. The section lit up is the nearest
// ancestor carrying that name, or the mark's own parent on a page where the
// tour has no such element.
//
// steps: several names, walked through with Back and Next, each lighting up
// its own element. For a "?" that explains more than the thing it sits in,
// such as every mark on a standings row.
function HelpMark({ step, steps, label, demo = false, size = 16 }) {
  const spotlightRef = useRef(null);

  // The overlay lives on <body>, so it would outlive a navigation away.
  useEffect(() => () => spotlightRef.current?.destroy(), []);

  const open = (e) => {
    e.stopPropagation();
    // The mark can sit inside the section a tour step lights up, so it can be
    // tapped while the page tour is on that step. Each driver draws its own
    // overlay and popover, so opening here would stack a second set on top.
    // The ref holds only a spotlight that is still open (onClose clears it),
    // so anything else marking the body active is the tour.
    if (!spotlightRef.current && document.body.classList.contains("driver-active")) return;

    const mark = e.currentTarget;
    const element = step ? mark.closest(`[data-tour="${step}"]`) || mark.parentElement : null;
    spotlightRef.current?.destroy();
    spotlightRef.current = spotlightSteps(steps || [step], {
      element,
      demo,
      onClose: () => { spotlightRef.current = null; },
    });
  };

  return (
    <button type="button" className="help-mark" aria-label={label} onClick={open}>
      <HelpCircle size={size} strokeWidth={1.8} />
    </button>
  );
}

export default HelpMark;
